/** OMNISYNIA — One Intelligence. Local, conservative spelling rules, v0.1.
 * This is a seed dictionary, not a general grammar model or a complete lexicon.
 * Never put contextual homophones (si/sí, tu/tú, haber/a ver) in this map.
 */
export const DICTIONARIES: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  es: {
    ortografia: "ortografía", ortografica: "ortográfica", ortografico: "ortográfico",
    correccion: "corrección", autocorreccion: "autocorrección",
    automaticamente: "automáticamente", automatica: "automática", automatico: "automático",
    opcion: "opción", informacion: "información",
    aplicacion: "aplicación", configuracion: "configuración", conversacion: "conversación",
    conexion: "conexión", creacion: "creación", generacion: "generación",
    grabacion: "grabación", reproduccion: "reproducción", cancion: "canción",
    musica: "música", musico: "músico", microfono: "micrófono", microfonos: "micrófonos",
    tambien: "también", ademas: "además", despues: "después", aqui: "aquí",
    asi: "así", atras: "atrás", facil: "fácil", dificil: "difícil",
    movil: "móvil", moviles: "móviles", pagina: "página", paginas: "páginas",
    boton: "botón", menu: "menú",
    isquierda: "izquierda", izquiera: "izquierda", selecion: "selección",
    selecionado: "seleccionado", selecionada: "seleccionada", selecionar: "seleccionar",
    selecionas: "seleccionas", seleciona: "selecciona", selecionando: "seleccionando",
    escojido: "escogido", escojida: "escogida", escojer: "escoger",
    nesesito: "necesito", nesecito: "necesito", nesesita: "necesita",
    nesesario: "necesario", profecional: "profesional", profecionales: "profesionales",
    desarollo: "desarrollo", desarollar: "desarrollar", funsiona: "funciona",
    funsionar: "funcionar", funsion: "función", funcion: "función",
    prueva: "prueba", pruevas: "pruebas", comprueva: "comprueba",
    berificar: "verificar", pantaya: "pantalla", imajen: "imagen", imajenes: "imágenes",
    imagenes: "imágenes", escrivir: "escribir", escrivo: "escribo",
    entonses: "entonces", abeces: "a veces", herror: "error", herrores: "errores",
    porfavor: "por favor", alluda: "ayuda", alludar: "ayudar", acesorio: "accesorio",
    inportante: "importante", inportar: "importar", conpartir: "compartir",
    canbiar: "cambiar", canbio: "cambio", conprobar: "comprobar",
    actualisacion: "actualización", actualizacion: "actualización", instalacion: "instalación",
    sincronizacion: "sincronización", notificacion: "notificación",
    evaluacion: "evaluación", explicacion: "explicación", traduccion: "traducción",
    suscripcion: "suscripción", suscripccion: "suscripción", subscripccion: "suscripción",
    tecnicamente: "técnicamente", tecnologico: "tecnológico", tecnologia: "tecnología",
    gramatica: "gramática", diccionarioo: "diccionario", escriviendo: "escribiendo"
  },
  en: {
    teh: "the", recieve: "receive", recieved: "received", seperate: "separate",
    definately: "definitely", occured: "occurred", occuring: "occurring",
    wierd: "weird", becuase: "because", langauge: "language", mesage: "message",
    succesful: "successful", succesfully: "successfully", adress: "address",
    enviroment: "environment", developement: "development", begining: "beginning",
    tommorow: "tomorrow", grammer: "grammar", thier: "their", reponse: "response"
  }
};

export interface OrthographyOptions {
  enabled?: boolean;
  language: string;
  protectedWords?: readonly string[];
  maxLength?: number;
  requireBoundary?: boolean;
}
export interface Correction {
  before: string;
  after: string;
  from: string;
  to: string;
  caretBefore: number;
  caretAfter: number;
}

const BRANDS = new Set(["omnisynia", "one", "core", "x10think", "qwen", "vocaloid"]);
const WORD = /[\p{L}\p{M}]+/u;

function caseLike(source: string, target: string): string | null {
  if (source === source.toLocaleLowerCase()) return target;
  if (source === source.toLocaleUpperCase()) return target.toLocaleUpperCase();
  if (source[0] === source[0].toLocaleUpperCase() && source.slice(1) === source.slice(1).toLocaleLowerCase())
    return target[0].toLocaleUpperCase() + target.slice(1);
  return null; // Preserve camelCase and unusual identifiers.
}

function protectedContext(text: string, start: number, end: number): boolean {
  const prefix = text.slice(0, start);
  // Conservatively leave code spans/fences and quoted text untouched.
  if ((prefix.match(/`/g)?.length ?? 0) % 2 !== 0) return true;
  if ((prefix.match(/"/g)?.length ?? 0) % 2 !== 0) return true;
  if ((prefix.match(/'/g)?.length ?? 0) % 2 !== 0) return true;
  if (prefix.lastIndexOf("«") > prefix.lastIndexOf("»") || prefix.lastIndexOf("“") > prefix.lastIndexOf("”")) return true;
  const left = text.slice(0, start).search(/\S*$/u);
  const rightMatch = text.slice(end).match(/^\S*/u)?.[0] ?? "";
  const token = text.slice(left, end) + rightMatch;
  const trimmed = token.replace(/[.,!?;:()]+$/u, "");
  if (/[\d@#_/\\=<>]|[\p{L}\p{M}]\.[\p{L}\p{M}]/u.test(trimmed)) return true;
  if (/[-+]/u.test(trimmed)) return true;
  const lineStart = text.lastIndexOf("\n", start - 1) + 1;
  const nextNewline = text.indexOf("\n", end);
  const line = text.slice(lineStart, nextNewline < 0 ? text.length : nextNewline);
  return /[{}=<>]|^\s*(?:const|let|var|def|class|import|from|return|function|SELECT|INSERT|UPDATE)\b/u.test(line);
}

/** Correct only the completed word immediately before the cursor, never the whole draft. */
export function correctAtCaret(text: string, caret: number, options: OrthographyOptions): Correction | null {
  if (options.enabled === false || !Number.isInteger(caret) || caret < 0 || caret > text.length) return null;
  const dictionary = DICTIONARIES[options.language.toLowerCase().split(/[-_]/)[0]];
  if (!dictionary) return null; // No Spanish fallback for unknown languages.
  const beforeCaret = text.slice(0, caret);
  const match = beforeCaret.match(/([\p{L}\p{M}]+)([\s,!?;:)\].]*)$/u);
  if (!match || ((options.requireBoundary ?? true) && !/[\s,!?;:)\]]/u.test(match[2]))) return null;
  const word = match[1];
  const start = caret - match[0].length;
  const end = start + word.length;
  // A partial word at a cursor position is never eligible.
  if (!match[2] && WORD.test(text[end] ?? "")) return null;
  const key = word.normalize("NFC").toLocaleLowerCase();
  if (BRANDS.has(key) || options.protectedWords?.some(w => w.normalize("NFC").toLocaleLowerCase() === key)) return null;
  if (!Object.prototype.hasOwnProperty.call(dictionary, key) || protectedContext(text, start, end)) return null;
  const replacement = caseLike(word, dictionary[key]);
  if (!replacement || replacement === word) return null;
  const after = text.slice(0, start) + replacement + text.slice(end);
  if (options.maxLength !== undefined && after.length > options.maxLength) return null;
  return { before: text, after, from: word, to: replacement, caretBefore: caret, caretAfter: caret + replacement.length - word.length };
}
